import { Avatar, Button, Listbox, ListboxItem, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, useGetUserOrgsQuery } from '@circuitry-ai/doc-data';
import { getUserInitials } from '../utils/commonutils';
import { useHoverMenu } from '../utiluicomponents/useHoverMenu';

interface OrgData {
  display_name: string;
  id: string;
  name: string;
}

export interface HeaderAccountControlsProps {
  onLogout?: () => void;
  onSwitchOrganisation?: (name: string) => void;
}

const menuItemClass = 'text-[14px] text-[#18181b] dark:text-foreground';

/** Two letters from a spaced or dotted name. NextUI shows `name` as-is when it is 4 characters or fewer. */
function accountInitials(name: string) {
  const parts = name.trim().split(/[\s.]+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return (parts[0] ?? '').slice(0, 2).toUpperCase();
}

export function HeaderAccountControls({ onLogout, onSwitchOrganisation }: HeaderAccountControlsProps) {
  const navigate = useNavigate();
  const { details: user } = useSelector((state: RootState) => state.auth.user);
  const token = useSelector((state: RootState) => state.auth.token);
  const { data: userOrgsList } = useGetUserOrgsQuery(user?.sub, { skip: !user?.sub || !token });
  const userName = user?.name ?? 'Guest';
  const profilePicture = user?.picture || undefined;
  const initials = accountInitials(userName);
  const currentOrg = String(user?.org_name ?? '');
  const tenantName = (userOrgsList ?? []).find(
    (org: OrgData) => org.name === currentOrg || org.display_name === currentOrg
  )?.display_name || currentOrg;
  const orgMenu = useHoverMenu();
  const accountMenu = useHoverMenu();

  return (
    <div className="flex items-center gap-2">
      <Popover placement="bottom-end" isOpen={orgMenu.isOpen} onOpenChange={orgMenu.setIsOpen} shouldBlockScroll={false}>
        <PopoverTrigger>
          <Button
            isIconOnly
            variant="light"
            radius="full"
            disableAnimation
            className="h-10 w-10 min-w-10 hover:bg-[#F4F4F5] aria-expanded:!scale-100 aria-expanded:!opacity-100 data-[pressed=true]:!scale-100 dark:hover:bg-content2"
            aria-label="Switch organization"
            onMouseEnter={orgMenu.open}
            onMouseLeave={orgMenu.closeSoon}
          >
            <Icon icon="lucide:layout-grid" width={17} height={17} className="text-[#181818] dark:text-[var(--cty-text-secondary)]" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="cty-header-menu max-h-[60vh] w-fit overflow-y-auto rounded-2xl border border-[#E4E4E7] p-4 shadow-[0_8px_32px_rgba(0,0,0,0.12)] dark:border-divider"
          onMouseEnter={orgMenu.open}
          onMouseLeave={orgMenu.closeSoon}
        >
          <Listbox
            aria-label="user-orgs"
            onAction={(selected) => {
              const name = selected.toString();
              const org = (userOrgsList ?? []).find((item: OrgData) => item.name === name);
              const alreadyActive = org
                ? org.name === currentOrg || org.display_name === currentOrg
                : name === currentOrg;
              if (!alreadyActive) onSwitchOrganisation?.(name);
              orgMenu.close();
            }}
            classNames={{ base: 'w-fit p-0', list: (userOrgsList ?? []).length === 1 ? 'cty-org-grid cty-org-grid-single' : 'cty-org-grid' }}
            itemClasses={{
              base: 'cty-org-option w-[150px] max-w-[150px] gap-2 rounded-xl border border-transparent px-2 py-3',
              title: 'min-w-0 truncate text-xs font-medium text-[#18181b] dark:text-foreground',
            }}
          >
            {(userOrgsList ?? []).map((org: OrgData) => {
              const selected = org.name === currentOrg || org.display_name === currentOrg;
              return (
                <ListboxItem
                  key={org.name}
                  textValue={org.display_name}
                  className={selected ? 'cty-org-selected border-primary bg-[#EFF6FF] dark:bg-content2' : ''}
                  classNames={{
                    title: selected
                      ? 'min-w-0 truncate text-xs !font-[600] !text-primary'
                      : 'min-w-0 truncate text-xs font-medium text-[#18181b] dark:text-foreground',
                  }}
                  startContent={
                    <Avatar
                      size="sm"
                      showFallback
                      name={getUserInitials(org.display_name)}
                      alt={org.display_name}
                      classNames={{ base: 'h-8 w-8 shrink-0 bg-[#F4F4F5] text-xs text-[#71717a]' }}
                    />
                  }
                >
                  <Tooltip content={org.display_name} placement="top" closeDelay={0}>
                    <span className={`block max-w-[90px] truncate text-xs ${selected ? 'font-semibold text-primary' : 'font-medium text-[#18181b] dark:text-foreground'}`}>
                      {org.display_name}
                    </span>
                  </Tooltip>
                </ListboxItem>
              );
            })}
          </Listbox>
        </PopoverContent>
      </Popover>

      <Popover placement="bottom-end" isOpen={accountMenu.isOpen} onOpenChange={accountMenu.setIsOpen} shouldBlockScroll={false}>
        <PopoverTrigger>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-full aria-expanded:scale-100 aria-expanded:opacity-100"
            aria-label="Account"
            onMouseEnter={accountMenu.open}
            onMouseLeave={accountMenu.closeSoon}
          >
            <Avatar
              size="sm"
              showFallback
              src={profilePicture}
              name={initials}
              alt="Profile"
              classNames={{ base: 'h-9 w-9 bg-success text-sm font-bold text-success-foreground' }}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="cty-header-menu min-w-[170px] w-fit items-stretch rounded-xl border border-[#E4E4E7] px-4 py-3.5 shadow-[0_8px_24px_rgba(0,0,0,0.10)] dark:border-divider"
          onMouseEnter={accountMenu.open}
          onMouseLeave={accountMenu.closeSoon}
        >
          <div className="mb-2.5 min-w-0">
            <div className={`truncate font-semibold ${menuItemClass}`}>{userName}</div>
            {tenantName ? <div className="truncate text-xs text-[#71717a]">{tenantName}</div> : null}
          </div>
          <hr className="border-divider" />
          <Listbox
            aria-label="Account"
            className="p-0 pt-2.5"
            onAction={(key) => {
              if (key === 'profile') navigate('/settings/profile');
              if (key === 'logout') onLogout?.();
              accountMenu.close();
            }}
            itemClasses={{
              base: 'gap-2.5 rounded-[8px] px-2 py-1.5 data-[hover=true]:bg-[#F4F4F5] dark:data-[hover=true]:bg-content2',
              title: menuItemClass,
            }}
          >
            <ListboxItem
              key="profile"
              textValue="Profile"
              title="Profile"
              startContent={<Icon icon="lucide:user" width={14} height={14} className="text-[#18181b] dark:text-[var(--cty-text-primary)]" />}
            />
            <ListboxItem
              key="logout"
              textValue="Logout"
              title="Logout"
              startContent={<Icon icon="lucide:log-out" width={14} height={14} className="text-[#18181b] dark:text-[var(--cty-text-primary)]" />}
            />
          </Listbox>
        </PopoverContent>
      </Popover>
    </div>
  );
}
